import React, { useState, useEffect } from "react";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { Spin, Pagination } from "antd";
import dayjs from "dayjs";
const PAYLOAD_KEY_ORDER = [
  "orgId",
  "transactionId",
  "businessName",
  "investorName",
  "virtualAccountNumber",
  "utr",
  "amount",
  "purpose",
  "status",
  "isDuplicate",
  "duplicateOfTransactionId",
];

const HIDDEN_PAYLOAD_KEYS = new Set([
  "previousstatus",
  "updatedstatus",
  "issuspicious",
  "isinvestor",
  "dealid",
  "txnchannel",
]);

const formatPurposeDisplay = (purpose, txnChannel) => {
  if (!purpose) return "";
  let text = String(purpose).trim().replace(/\brecived\b/gi, "received");
  text = text
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
  if (txnChannel) {
    text = `${text} (${String(txnChannel).trim().toUpperCase()})`;
  }
  return text;
};

const PaymentActivityLog = ({ transactionId }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs = [], loading = false, totalRecords = 0 } =
    useSelector((state) => state?.activityLog) || {};

  useEffect(() => {
    if (transactionId) {
      dispatch(getActivityLog({ module: "PAYMENTS", entityId: transactionId, page, limit }));
    }
  }, [dispatch, transactionId, page]);

  const formatPayloadValue = (key, value, payload = {}) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "object") return JSON.stringify(value);

    if (key === "purpose") {
      return formatPurposeDisplay(value, payload.txnChannel);
    }

    const strVal = String(value).trim();

    if (key === "amount") {
      const num = Number(strVal.replace(/,/g, ""));
      if (!Number.isNaN(num)) {
        return Number(num).toLocaleString("en-IN");
      }
    }

    if (key === "isDuplicate") {
      return value === true || value === "true" ? "Yes" : "No";
    }

    return strVal;
  };

  const filterPayloadEntries = (payload = {}) => {
    if (!payload || typeof payload !== "object") return [];

    const hasInvestorName =
      payload.investorName && String(payload.investorName).trim() && payload.investorName !== "N/A";
    const hasBusinessName =
      payload.businessName && String(payload.businessName).trim() && payload.businessName !== "N/A";

    const isVisible = (key, value) => {
      const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (HIDDEN_PAYLOAD_KEYS.has(lowerKey)) return false;
      if (value === null || value === undefined || value === "") return false;
      if (value === "N/A") return false;

      // Business → Business Name only; Investor → Investor Name only
      if (key === "investorName") return Boolean(hasInvestorName);
      if (key === "businessName") return Boolean(hasBusinessName);

      return true;
    };

    // Exact display order, one-by-one
    const ordered = [];
    PAYLOAD_KEY_ORDER.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) return;
      if (!isVisible(key, payload[key])) return;
      ordered.push([key, payload[key]]);
    });

    return ordered;
  };

  const formatPayloadKey = (key) => {
    const labels = {
      orgId: "Org Id",
      transactionId: "Transaction Id",
      businessName: "Business Name",
      investorName: "Investor Name",
      virtualAccountNumber: "Virtual Account Number",
      utr: "Utr",
      amount: "Amount",
      purpose: "Purpose",
      status: "Status",
      isDuplicate: "Duplicate",
      duplicateOfTransactionId: "Duplicate Of Transaction ID",
    };
    if (labels[key]) return labels[key];
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  };

  const getActionConfig = (action) => {
    switch (action) {
      case "DEPOSIT_RECORDED":
        return { label: "Deposit Recorded", color: "#e6ffed", textColor: "#22c55e" };
      case "DEPOSIT_MARKED_DUPLICATE":
        return { label: "Marked Duplicate", color: "#fff1f0", textColor: "#f5222d" };
      default:
        return {
          label: action.replace(/_/g, " "),
          color: "#e6f7ff",
          textColor: "#1890ff",
        };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const txnRef = log.payload?.transactionId || transactionId || "transaction";
    const channel = log.payload?.txnChannel ? ` via <strong>${log.payload.txnChannel}</strong>` : "";
    const business =
      log.payload?.businessName && log.payload.businessName !== "N/A"
        ? log.payload.businessName
        : null;
    const investor =
      log.payload?.investorName && log.payload.investorName !== "N/A"
        ? log.payload.investorName
        : null;
    const party = investor
      ? ` for investor <strong>"${investor}"</strong>`
      : business
        ? ` for business <strong>"${business}"</strong>`
        : "";

    if (log.action === "DEPOSIT_RECORDED") {
      return `<strong>${name}</strong> (${role}) <strong>RECORDED DEPOSIT</strong>${channel}${party} for transaction <strong>"${txnRef}"</strong>`;
    }
    if (log.action === "DEPOSIT_MARKED_DUPLICATE") {
      const original = log.payload?.duplicateOfTransactionId;
      const dupNote = original
        ? ` (duplicate of <strong>"${original}"</strong>)`
        : "";
      return `<strong>${name}</strong> (${role}) <strong>MARKED DEPOSIT AS DUPLICATE</strong>${dupNote}${party} for transaction <strong>"${txnRef}"</strong>`;
    }

    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>${actionConfig.label.toUpperCase()}</strong> on transaction <strong>"${txnRef}"</strong>`;
  };

  return (
    <div style={{ padding: "24px 0", maxWidth: "900px", margin: "0 auto", fontFamily: "Inter, sans-serif" }}>
      <div style={{ border: "1px solid #f0f0f0", borderRadius: "8px", overflow: "hidden", background: "#ffffff" }}>
        <div
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            background: "#fafafa",
            borderBottom: isCollapsed ? "none" : "1px solid #f0f0f0",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#262626" }}>
              Deposit Activity History
            </span>
            <span
              style={{
                background: "#e8e8e8",
                color: "#595959",
                fontSize: "12px",
                fontWeight: "600",
                padding: "2px 8px",
                borderRadius: "4px",
              }}
            >
              {totalRecords}
            </span>
          </div>
          <div>
            {isCollapsed ? (
              <DownOutlined style={{ fontSize: "14px", color: "#595959" }} />
            ) : (
              <UpOutlined style={{ fontSize: "14px", color: "#595959" }} />
            )}
          </div>
        </div>

        {!isCollapsed && (
          <div style={{ padding: "24px 24px 8px 24px" }}>
            <Spin spinning={loading}>
              {logs?.length === 0 ? (
                <div style={{ textAlign: "center", color: "#8c8c8c", padding: "20px 0" }}>
                  No activity logs found.
                </div>
              ) : (
                logs?.map((log, index) => {
                  const isLast = index === logs.length - 1;
                  const actionConfig = getActionConfig(log.action);
                  const dateStr = dayjs(log.createdAt).format("DD MMM YYYY");
                  const timeStr = dayjs(log.createdAt).format("hh:mm A");
                  const payloadEntries = filterPayloadEntries(log.payload);

                  return (
                    <div key={log.logId || index} style={{ display: "flex", minHeight: "80px" }}>
                      <div style={{ width: "90px", textAlign: "right", paddingRight: "20px", flexShrink: 0 }}>
                        <div style={{ fontSize: "12px", fontWeight: "500", color: "#262626" }}>{dateStr}</div>
                        <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "2px" }}>{timeStr}</div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "20px", flexShrink: 0 }}>
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            border: `3px solid ${actionConfig.textColor}`,
                            background: "#ffffff",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            boxSizing: "border-box",
                          }}
                        >
                          <div
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: actionConfig.textColor,
                            }}
                          />
                        </div>

                        {!isLast && (
                          <div
                            style={{
                              width: "3px",
                              background: actionConfig.textColor,
                              flexGrow: 1,
                              marginTop: "4px",
                              marginBottom: "4px",
                            }}
                          />
                        )}
                      </div>

                      <div style={{ paddingLeft: "20px", paddingBottom: "24px", flexGrow: 1 }}>
                        <div
                          style={{ fontSize: "13px", color: "#434343", lineHeight: "1.5" }}
                          dangerouslySetInnerHTML={{ __html: formatDesc(log) }}
                        />

                        <div style={{ display: "flex", alignItems: "center", gap: "24px", marginTop: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Action:</span>
                            <span
                              style={{
                                background: actionConfig.color,
                                color: actionConfig.textColor,
                                fontSize: "11px",
                                fontWeight: "700",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                padding: "2px 8px",
                                borderRadius: "4px",
                              }}
                            >
                              {actionConfig.label}
                            </span>
                          </div>
                        </div>

                        {payloadEntries.length > 0 && (
                          <div
                            style={{
                              marginTop: 10,
                              padding: "10px 16px",
                              background: "#fafafa",
                              border: "1px solid #f0f0f0",
                              borderRadius: "6px",
                              fontSize: "12px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.01)",
                            }}
                          >
                            {payloadEntries.map(([key, value]) => (
                              <div key={key} style={{ marginBottom: "6px" }}>
                                <span style={{ fontWeight: "600", color: "#595959" }}>
                                  {formatPayloadKey(key)}:
                                </span>{" "}
                                <span style={{ color: "#262626" }}>{formatPayloadValue(key, value, log.payload)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </Spin>
            {totalRecords > limit && (
              <div style={{ textAlign: "right", marginTop: 10 }}>
                <Pagination
                  current={page}
                  total={totalRecords}
                  pageSize={limit}
                  onChange={(p) => setPage(p)}
                  size="small"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentActivityLog;
